import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface SourceOption {
  value: string;
  title: string;
  description: string;
  isLatest: boolean;
}

interface SourceOptionListProps {
  label: string;
  options: Array<SourceOption>;
  value?: string;
  onValueChange: (value: string) => void;
  isLoading: boolean;
  disabled?: boolean;
}

const SourceOptionList: FC<SourceOptionListProps> = ({
  label,
  options,
  value,
  onValueChange,
  isLoading,
  disabled,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className='flex flex-col gap-1'>
        <Skeleton className='h-16 w-full' />
        <Skeleton className='h-16 w-full' />
      </div>
    );
  }

  if (options.length === 0) {
    return (
      <p className='px-3 py-6 text-center text-sm text-muted-foreground'>
        {t('scenarios.create.noUploads')}
      </p>
    );
  }

  return (
    <RadioGroup
      aria-label={label}
      value={value ?? ''}
      onValueChange={onValueChange}
      disabled={disabled}
      className='max-h-80 grid-cols-[minmax(0,1fr)] gap-1 overflow-y-auto'>
      {options.map((option) => {
        const id = `source-option-${option.value}`;
        return (
          <label
            key={option.value}
            htmlFor={id}
            className={cn(
              'flex cursor-pointer items-center gap-3 rounded-lg border border-transparent p-3 hover:bg-muted/50',
              'has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-muted/50',
              disabled && 'cursor-not-allowed opacity-50'
            )}>
            <RadioGroupItem
              id={id}
              value={option.value}
            />
            <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
              <span className='truncate text-sm font-medium'>
                {option.title}
              </span>
              <span className='text-xs text-muted-foreground'>
                {option.description}
              </span>
            </span>
            {option.isLatest && <Badge>{t('scenarios.create.latest')}</Badge>}
          </label>
        );
      })}
    </RadioGroup>
  );
};

export default SourceOptionList;
